import { useState, type FormEvent } from "react";
import { useAuth } from "../auth/AuthProvider";
import { PageHeader } from "../components/ui";

const inputClass =
  "w-full rounded-lg border border-ink/10 bg-paper px-4 py-3 text-[14.5px] text-ink transition placeholder:text-ink-4/60 focus:border-tide focus:outline-none focus:ring-[3px] focus:ring-tide/15";

const labelClass =
  "mb-2 block text-[11.5px] font-semibold uppercase tracking-wider text-ink-4";

const buttonClass =
  "mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-tide px-3 py-3 text-[14.5px] font-medium text-paper-hi transition hover:-translate-y-px hover:bg-tide-d hover:shadow-[0_10px_30px_-8px_rgba(38,99,199,0.35)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:bg-tide disabled:hover:shadow-none";

/** User-facing messages keyed on machine-readable API `code`. */
const ERROR_MESSAGES: Record<string, string> = {
  invalid_password: "Current password is incorrect.",
  same_email: "New email must be different from your current email.",
  email_taken: "That email address is already in use.",
  rate_limited: "Too many requests. Please try again later.",
  superadmin_restricted:
    "Superadmin email changes must be performed by a peer administrator.",
};

export function Account() {
  const { user } = useAuth();

  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch("/auth/email/change-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          new_email: newEmail,
          current_password: currentPassword,
        }),
      });
      const data = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        code?: string;
      };
      if (!response.ok || !data.ok) {
        const code = data.code ?? "";
        setError(
          ERROR_MESSAGES[code] ||
            data.error ||
            "Something went wrong. Please try again.",
        );
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
      setSent(true);
    } catch {
      setError("Could not reach the server. Please try again.");
      setSubmitting(false);
    }
  }

  return (
    <div>
      <PageHeader />
      <div className="mx-auto w-full max-w-xl px-4 pb-16 md:px-6">

      {/* Current email */}
      <div className="mt-8">
        <div className={labelClass}>Current email</div>
        <div className="text-[14.5px] text-ink">{user?.email}</div>
      </div>

      {/* Change email form */}
      <div className="mt-10">
        <h2 className="text-[16px] font-medium tracking-tight">
          Change email address
        </h2>

        {sent ? (
          <div className="mt-5 rounded-2xl border border-ink/5 bg-paper-hi p-6 text-center">
            <p className="text-[14.5px] leading-relaxed text-ink-3">
              Check your new email address for a confirmation link. The link
              expires in 60 minutes and can be used once.
            </p>
            <p className="mt-4 text-[13px] text-ink-4">
              No email after a few minutes? Check your spam folder, or{" "}
              <button
                type="button"
                onClick={() => {
                  setSent(false);
                  setNewEmail("");
                  setCurrentPassword("");
                }}
                className="font-medium text-tide hover:underline"
              >
                try again
              </button>
              .
            </p>
          </div>
        ) : (
          <form
            className="mt-5 space-y-4"
            onSubmit={handleSubmit}
            noValidate
          >
            <div>
              <label htmlFor="account-new-email" className={labelClass}>
                New email
              </label>
              <input
                id="account-new-email"
                type="email"
                autoComplete="email"
                required
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="you@example.com"
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="account-current-password" className={labelClass}>
                Current password
              </label>
              <div className="relative">
                <input
                  id="account-current-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter your password"
                  className={inputClass + " pr-10"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-4 transition hover:text-ink"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-lg border border-rust/30 bg-rust-s/40 px-3 py-2 text-[13px] text-rust"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !newEmail || !currentPassword}
              className={buttonClass}
            >
              {submitting ? "Sending..." : "Send confirmation email"}
            </button>
          </form>
        )}
      </div>
      </div>
    </div>
  );
}
